import {
  LFO_SHAPES,
  MOD_DESTINATIONS,
  MOD_SOURCES,
  PARAM_PRESENTATION,
  PARAM_RANGES,
  PRESET_STYLES,
  WAVETABLE_GENERATORS,
  type LfoShapeName,
  type ModDestination,
  type ModSource,
  type ParamName,
  type PresetModel,
  type PresetStyle,
  type WavetableGeneratorName,
} from "@defibrillator/preset-core";
import {
  AdsrEditor,
  Knob,
  ModMatrix,
  modCellKey,
  Slider,
  type AdsrValue,
} from "@defibrillator/ui";
import { useMemo, useState } from "react";

export type PresetControlsProps = {
  model: PresetModel;
  onChange: (next: PresetModel) => void;
  disabled?: boolean;
};

function formatParam(param: ParamName, value: number): string {
  const meta = PARAM_PRESENTATION[param];
  const rounded =
    meta.integer || meta.boolean
      ? String(Math.round(value))
      : String(Math.round(value * 1000) / 1000);
  return meta.unit ? `${rounded} ${meta.unit}` : rounded;
}

function ParamKnob({
  model,
  param,
  onChange,
  disabled,
}: {
  model: PresetModel;
  param: ParamName;
  onChange: (next: PresetModel) => void;
  disabled?: boolean;
}) {
  const range = PARAM_RANGES[param];
  const meta = PARAM_PRESENTATION[param];
  if (meta.boolean) {
    const on = model.params[param] >= 0.5;
    return (
      <label className="editor-toggle" title={meta.description}>
        <input
          type="checkbox"
          checked={on}
          disabled={disabled}
          aria-label={meta.label}
          aria-description={meta.description}
          onChange={(e) => {
            onChange({
              ...model,
              params: { ...model.params, [param]: e.target.checked ? 1 : 0 },
            });
          }}
        />
        <span>{meta.label}</span>
      </label>
    );
  }
  return (
    <div title={meta.description}>
      <Knob
        label={meta.label}
        value={model.params[param]}
        min={range.min}
        max={range.max}
        step={meta.step}
        defaultValue={range.default}
        scale={meta.scale ?? "linear"}
        format={(v) => formatParam(param, v)}
        disabled={disabled}
        onChange={(value) => {
          onChange({
            ...model,
            params: { ...model.params, [param]: value },
          });
        }}
      />
    </div>
  );
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details className="editor-section" open={defaultOpen}>
      <summary>{title}</summary>
      <div className="editor-section-body">{children}</div>
    </details>
  );
}

export function PresetControls({ model, onChange, disabled }: PresetControlsProps) {
  const [selectedMod, setSelectedMod] = useState<{
    source: string;
    destination: string;
  } | null>(null);
  const [showAdvancedLfo, setShowAdvancedLfo] = useState(false);

  const amounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const mod of model.modulations) {
      map[modCellKey(mod.source, mod.destination)] = mod.amount;
    }
    return map;
  }, [model.modulations]);

  const selectedSlot = selectedMod
    ? model.modulations.find(
        (m) => m.source === selectedMod.source && m.destination === selectedMod.destination,
      )
    : undefined;

  function setParamGroup(updates: Partial<Record<ParamName, number>>) {
    onChange({
      ...model,
      params: { ...model.params, ...updates },
    });
  }

  function setEnvelope(which: 1 | 2, value: AdsrValue) {
    const clampParam = (param: ParamName, raw: number) => {
      const range = PARAM_RANGES[param];
      return Math.min(range.max, Math.max(range.min, raw));
    };
    if (which === 1) {
      setParamGroup({
        env1_attack: clampParam("env1_attack", value.attack),
        env1_decay: clampParam("env1_decay", value.decay),
        env1_sustain: clampParam("env1_sustain", value.sustain),
        env1_release: clampParam("env1_release", value.release),
      });
    } else {
      setParamGroup({
        env2_attack: clampParam("env2_attack", value.attack),
        env2_decay: clampParam("env2_decay", value.decay),
        env2_sustain: clampParam("env2_sustain", value.sustain),
        env2_release: clampParam("env2_release", value.release),
      });
    }
  }

  function upsertModulation(source: ModSource, destination: ModDestination, amount: number) {
    const existing = model.modulations.find(
      (m) => m.source === source && m.destination === destination,
    );
    let nextMods = model.modulations.filter(
      (m) => !(m.source === source && m.destination === destination),
    );
    if (Math.abs(amount) < 1e-6) {
      onChange({ ...model, modulations: nextMods });
      return;
    }
    const used = new Set(nextMods.map((m) => m.slot));
    let slot = existing?.slot ?? 1;
    if (!existing) {
      slot = 1;
      while (used.has(slot) && slot <= 64) slot += 1;
    }
    if (slot > 64 || nextMods.length >= 24) return;
    nextMods = [
      ...nextMods,
      {
        slot,
        source,
        destination,
        amount,
        bipolar: existing?.bipolar ?? false,
      },
    ].sort((a, b) => a.slot - b.slot);
    onChange({ ...model, modulations: nextMods });
  }

  const oscParams = (n: 1 | 2 | 3): ParamName[] => [
    `osc${n}_level`,
    `osc${n}_transpose`,
    `osc${n}_tune`,
    `osc${n}_pan`,
    `osc${n}_unison_voices`,
    `osc${n}_unison_detune`,
    `osc${n}_wave_frame`,
    `osc${n}_phase`,
    `osc${n}_random_phase`,
  ];

  return (
    <div className="preset-controls">
      <Section title="Preset">
        <div className="editor-meta-grid">
          <label>
            Name
            <input
              className="editor-input"
              value={model.name}
              disabled={disabled}
              maxLength={64}
              onChange={(e) => onChange({ ...model, name: e.target.value })}
            />
          </label>
          <label>
            Style
            <select
              className="editor-input"
              value={model.style}
              disabled={disabled}
              onChange={(e) =>
                onChange({ ...model, style: e.target.value as PresetStyle })
              }
            >
              {PRESET_STYLES.map((style) => (
                <option key={style} value={style}>
                  {style}
                </option>
              ))}
            </select>
          </label>
          <label className="editor-span-2">
            Comments
            <textarea
              className="editor-input"
              value={model.comments}
              disabled={disabled}
              maxLength={500}
              rows={2}
              onChange={(e) => onChange({ ...model, comments: e.target.value })}
            />
          </label>
        </div>
      </Section>

      <Section title="Voice and play mode">
        <p className="editor-hint">
          Polyphony, mono/legato, glide, and global transpose. Pitch sequences usually target voice
          transpose.
        </p>
        <div className="editor-knob-row">
          {(
            ["polyphony", "voice_mode", "voice_transpose", "glide_time", "glide_force"] as ParamName[]
          ).map((param) => (
            <ParamKnob
              key={param}
              model={model}
              param={param}
              onChange={onChange}
              disabled={disabled}
            />
          ))}
        </div>
      </Section>

      {([1, 2, 3] as const).map((osc) => (
        <Section key={osc} title={`Oscillator ${osc}`}>
          <div className="editor-knob-row">
            {oscParams(osc).map((param) => (
              <ParamKnob
                key={param}
                model={model}
                param={param}
                onChange={onChange}
                disabled={disabled}
              />
            ))}
          </div>
          <label>
            Wavetable
            <select
              className="editor-input"
              disabled={disabled}
              value={
                model.wavetables[osc].kind === "basic"
                  ? model.wavetables[osc].shape
                  : model.wavetables[osc].kind
              }
              onChange={(e) => {
                const value = e.target.value;
                const next = structuredClone(model);
                if (value === "harmonic") {
                  next.wavetables[osc] = {
                    kind: "harmonic",
                    harmonics: [1, 0.5, 0.25, 0.125],
                  };
                } else if (value === "pulse") {
                  next.wavetables[osc] = { kind: "pulse", width: 0.5 };
                } else {
                  next.wavetables[osc] = {
                    kind: "basic",
                    shape: value as WavetableGeneratorName,
                  };
                }
                onChange(next);
              }}
            >
              {WAVETABLE_GENERATORS.map((shape) => (
                <option key={shape} value={shape}>
                  {shape}
                </option>
              ))}
              <option value="harmonic">harmonic (custom)</option>
              <option value="pulse">pulse (width)</option>
            </select>
          </label>
          {model.wavetables[osc].kind === "pulse" ? (
            <Slider
              label="Pulse width"
              min={0.05}
              max={0.95}
              step={0.01}
              value={model.wavetables[osc].width}
              disabled={disabled}
              onChange={(width) => {
                const next = structuredClone(model);
                next.wavetables[osc] = { kind: "pulse", width };
                onChange(next);
              }}
            />
          ) : null}
        </Section>
      ))}

      <Section title="Filter">
        <div className="editor-knob-row">
          {(["filter_on", "filter_cutoff", "filter_resonance", "filter_blend"] as ParamName[]).map(
            (param) => (
              <ParamKnob
                key={param}
                model={model}
                param={param}
                onChange={onChange}
                disabled={disabled}
              />
            ),
          )}
        </div>
      </Section>

      <Section title="Envelopes">
        <div className="editor-env-grid">
          <div>
            <AdsrEditor
              label="Envelope 1"
              maxTime={PARAM_RANGES.env1_release.max}
              step={0.001}
              value={{
                attack: model.params.env1_attack,
                decay: model.params.env1_decay,
                sustain: model.params.env1_sustain,
                release: model.params.env1_release,
              }}
              onChange={(value) => setEnvelope(1, value)}
            />
            <div className="editor-knob-row">
              {(["env1_attack", "env1_decay", "env1_sustain", "env1_release"] as ParamName[]).map(
                (param) => (
                  <ParamKnob
                    key={param}
                    model={model}
                    param={param}
                    onChange={onChange}
                    disabled={disabled}
                  />
                ),
              )}
            </div>
          </div>
          <div>
            <AdsrEditor
              label="Envelope 2"
              maxTime={2.5}
              step={0.001}
              value={{
                attack: model.params.env2_attack,
                decay: model.params.env2_decay,
                sustain: model.params.env2_sustain,
                release: model.params.env2_release,
              }}
              onChange={(value) => setEnvelope(2, value)}
            />
            <div className="editor-knob-row">
              {(["env2_attack", "env2_decay", "env2_sustain", "env2_release"] as ParamName[]).map(
                (param) => (
                  <ParamKnob
                    key={param}
                    model={model}
                    param={param}
                    onChange={onChange}
                    disabled={disabled}
                  />
                ),
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section title="LFOs">
        <div className="editor-knob-row">
          {(["lfo1_frequency", "lfo2_frequency", "lfo3_frequency", "lfo4_frequency"] as ParamName[]).map(
            (param) => (
              <ParamKnob
                key={param}
                model={model}
                param={param}
                onChange={onChange}
                disabled={disabled}
              />
            ),
          )}
        </div>
        <div className="editor-meta-grid">
          {([1, 2, 3, 4] as const).map((index) => (
            <label key={index}>
              LFO {index} shape
              <select
                className="editor-input"
                disabled={disabled}
                value={model.lfoShapes[index]}
                onChange={(e) => {
                  onChange({
                    ...model,
                    lfoShapes: {
                      ...model.lfoShapes,
                      [index]: e.target.value as LfoShapeName,
                    },
                  });
                }}
              >
                {LFO_SHAPES.map((shape) => (
                  <option key={shape} value={shape}>
                    {shape}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
        <button
          type="button"
          className="btn ghost btn-compact"
          onClick={() => setShowAdvancedLfo((v) => !v)}
        >
          {showAdvancedLfo ? "Hide advanced LFOs" : "Advanced LFOs 5–8"}
        </button>
        {showAdvancedLfo ? (
          <div className="editor-meta-grid">
            {([5, 6, 7, 8] as const).map((index) => (
              <label key={index}>
                LFO {index} shape
                <select
                  className="editor-input"
                  disabled={disabled}
                  value={model.lfoShapes[index]}
                  onChange={(e) => {
                    onChange({
                      ...model,
                      lfoShapes: {
                        ...model.lfoShapes,
                        [index]: e.target.value as LfoShapeName,
                      },
                    });
                  }}
                >
                  {LFO_SHAPES.map((shape) => (
                    <option key={shape} value={shape}>
                      {shape}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        ) : null}
      </Section>

      <Section title="Output">
        <div className="editor-knob-row">
          <ParamKnob model={model} param="volume" onChange={onChange} disabled={disabled} />
        </div>
      </Section>

      <Section title="Effects">
        <p className="editor-hint">
          Space, width, and finish. Keep wet/feedback conservative for playable patches.
        </p>
        <div className="editor-knob-row">
          {(
            [
              "delay_on",
              "delay_dry_wet",
              "delay_feedback",
              "reverb_on",
              "reverb_dry_wet",
              "reverb_size",
              "chorus_on",
              "chorus_dry_wet",
              "phaser_on",
              "phaser_dry_wet",
              "distortion_on",
              "distortion_drive",
              "distortion_mix",
            ] as ParamName[]
          ).map((param) => (
            <ParamKnob
              key={param}
              model={model}
              param={param}
              onChange={onChange}
              disabled={disabled}
            />
          ))}
        </div>
      </Section>

      <Section title="Sequence" defaultOpen={model.sequences.length > 0}>
        <p className="editor-hint">
          Held-note pitch/gate motion owned by the preset (not a MIDI arpeggiator). Resources marked
          ownedBySequence are allocated by the compiler.
        </p>
        {model.sequences.length === 0 ? (
          <p className="editor-hint">No sequence on this preset.</p>
        ) : (
          model.sequences.map((seq) => (
            <div key={seq.index} className="editor-sequence-block">
              <strong>
                Sequence {seq.index} · {seq.division} · {seq.steps.length} steps · {seq.retrigger}
              </strong>
              <div className="editor-sequence-grid" role="grid" aria-label={`Sequence ${seq.index}`}>
                {seq.steps.map((step, i) => (
                  <label key={i} className="editor-sequence-step">
                    <span>#{i + 1}</span>
                    <input
                      type="number"
                      className="editor-input"
                      disabled={disabled}
                      value={step.semitones}
                      min={-24}
                      max={24}
                      step={1}
                      aria-label={`Step ${i + 1} semitones`}
                      onChange={(e) => {
                        const next = structuredClone(model);
                        const target = next.sequences.find((s) => s.index === seq.index);
                        if (!target) return;
                        target.steps[i] = {
                          ...target.steps[i]!,
                          semitones: Number(e.target.value),
                        };
                        onChange(next);
                      }}
                    />
                    <input
                      type="number"
                      className="editor-input"
                      disabled={disabled}
                      value={step.gate}
                      min={0}
                      max={1}
                      step={0.05}
                      aria-label={`Step ${i + 1} gate`}
                      onChange={(e) => {
                        const next = structuredClone(model);
                        const target = next.sequences.find((s) => s.index === seq.index);
                        if (!target) return;
                        target.steps[i] = {
                          ...target.steps[i]!,
                          gate: Number(e.target.value),
                        };
                        onChange(next);
                      }}
                    />
                  </label>
                ))}
              </div>
              <p className="editor-hint">
                Uses LFO {seq.allocation.pitchLfo}/{seq.allocation.gateLfo} and mod slots{" "}
                {seq.allocation.pitchModSlot}/{seq.allocation.gateModSlot}.
              </p>
            </div>
          ))
        )}
      </Section>

      <Section title="Macros">
        <p className="editor-hint">Named performance controls with coordinated destinations.</p>
        <div className="editor-meta-grid">
          {(model.macros ?? []).map((macro) => (
            <label key={macro.index}>
              Macro {macro.index} name
              <input
                className="editor-input"
                disabled={disabled}
                value={macro.name}
                maxLength={64}
                onChange={(e) => {
                  onChange({
                    ...model,
                    macros: model.macros.map((m) =>
                      m.index === macro.index ? { ...m, name: e.target.value } : m,
                    ),
                  });
                }}
              />
              <span className="editor-hint">
                {macro.assignments.length} assignment{macro.assignments.length === 1 ? "" : "s"}
              </span>
            </label>
          ))}
        </div>
        <div className="editor-knob-row">
          {(
            [
              "macro_control_1",
              "macro_control_2",
              "macro_control_3",
              "macro_control_4",
            ] as ParamName[]
          ).map((param) => (
            <ParamKnob
              key={param}
              model={model}
              param={param}
              onChange={onChange}
              disabled={disabled}
            />
          ))}
        </div>
      </Section>

      <Section title="Modulation">
        <p className="editor-hint">
          Wire envelopes, LFOs, sequences, and macros to musical destinations. Sequence-owned routes
          are listed in the matrix after compile/normalize.
        </p>
        <ModMatrix
          sources={[...MOD_SOURCES]}
          destinations={[...MOD_DESTINATIONS]}
          amounts={amounts}
          selected={selectedMod}
          onSelect={setSelectedMod}
          onChangeAmount={(cell, amount) => {
            setSelectedMod(cell);
            upsertModulation(cell.source as ModSource, cell.destination as ModDestination, amount);
          }}
        />
        {selectedMod ? (
          <div className="editor-mod-detail">
            <Slider
              label={`${selectedMod.source} → ${selectedMod.destination}`}
              min={-1}
              max={1}
              step={0.01}
              value={selectedSlot?.amount ?? 0}
              disabled={disabled}
              onChange={(amount) =>
                upsertModulation(
                  selectedMod.source as ModSource,
                  selectedMod.destination as ModDestination,
                  amount,
                )
              }
            />
            <label className="editor-toggle">
              <input
                type="checkbox"
                checked={selectedSlot?.bipolar ?? false}
                disabled={disabled || !selectedSlot}
                onChange={(e) => {
                  if (!selectedSlot) return;
                  onChange({
                    ...model,
                    modulations: model.modulations.map((m) =>
                      m.slot === selectedSlot.slot
                        ? { ...m, bipolar: e.target.checked }
                        : m,
                    ),
                  });
                }}
              />
              <span>Bipolar</span>
            </label>
            {selectedSlot?.ownedBySequence != null ? (
              <p className="editor-hint">Owned by sequence {selectedSlot.ownedBySequence}</p>
            ) : null}
            <button
              type="button"
              className="btn ghost btn-compact"
              disabled={disabled || !selectedSlot}
              onClick={() => {
                if (!selectedSlot) return;
                onChange({
                  ...model,
                  modulations: model.modulations.filter((m) => m.slot !== selectedSlot.slot),
                });
              }}
            >
              Remove route
            </button>
          </div>
        ) : (
          <p className="editor-hint">Select a cell to edit amount, bipolar, or remove.</p>
        )}
      </Section>
    </div>
  );
}
